import type {
  AccountApi,
  AuthResponse,
  LoginPayload as LoginCredentials,
  LoginResult,
  RegisterPayload as RegisterData,
} from "@membentry/shared-api";
import {
  type UserDetailDTO,
  type WorkspaceAccess,
  type WorkspaceKey,
} from "@membentry/shared-types";
import {
  useMutation,
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";

export type { AuthResponse, LoginCredentials, LoginResult, RegisterData };

export const CURRENT_USER_QUERY_KEY = ["current-user"] as const;

export interface AuthStore<T = unknown> {
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUserData: (user: T) => void;
  clearAuthState: () => void;
  setMfaRequired?: (required: boolean, email?: string) => void;
  setRequiresMfaSetup?: (required: boolean) => void;
  setMfaSetupRequired?: (required: boolean) => void;
  clearMfaState?: () => void;
}

export interface AuthHooksOptions<
  T = unknown,
  L = LoginCredentials,
  R = RegisterData,
> {
  authStore: AuthStore<T>;
  loginFn: (data: L) => Promise<LoginResult<T>>;
  registerFn: (data: R) => Promise<void>;
  onError?: (error: Error) => void;
  onLoginError?: (error: Error) => void;
  onRegisterError?: (error: Error) => void;
}

export interface CreateAuthHooksOptions
  extends Omit<
    AuthHooksOptions<UserDetailDTO, LoginCredentials, RegisterData>,
    "loginFn" | "registerFn"
  > {
  accountApi: Pick<AccountApi, "login" | "register">;
}

const OWNER_ROLE = "Owner";
const SUPERADMIN_ROLE = "SuperAdmin";

/**
 * Single place that applies a LoginResult to the auth store: token pair +
 * user on success, MFA-challenge flags otherwise. Shared by the password
 * login mutation and by social provider sign-in (same envelope contract).
 */
export function applyLoginResultToAuthStore<T>(
  authStore: AuthStore<T>,
  data: LoginResult<T>,
): void {
  if (data.success) {
    authStore.setTokens(
      data.authResponse.accessToken,
      data.authResponse.refreshToken,
    );
    if (data.authResponse.user) {
      authStore.setUserData(data.authResponse.user);
    }
  } else if ("requiresMfa" in data) {
    authStore.setMfaRequired?.(true, data.email);
  } else if ("requiresMfaSetup" in data) {
    authStore.setTokens(
      data.authResponse.accessToken,
      data.authResponse.refreshToken,
    );
    if (data.authResponse.user) {
      authStore.setUserData(data.authResponse.user);
    }
    authStore.setMfaSetupRequired?.(true);
    authStore.setRequiresMfaSetup?.(true);
  }
}

const deriveWorkspaceAccess = (
  roles: readonly string[] | null | undefined,
): WorkspaceAccess => {
  const roleSet = new Set(roles ?? []);
  const available: WorkspaceKey[] = [];
  const canAccessOwnerWorkspace = roleSet.has(OWNER_ROLE);
  const canAccessSuperadminWorkspace = roleSet.has(SUPERADMIN_ROLE);

  if (canAccessOwnerWorkspace) {
    available.push("owner");
  }

  if (canAccessSuperadminWorkspace) {
    available.push("superadmin");
  }

  return {
    available,
    defaultWorkspace: available.length === 1 ? available[0] : null,
    canAccessOwnerWorkspace,
    canAccessSuperadminWorkspace,
  };
};

const normalizeUserWorkspaceAccess = <T extends { roles: string[] }>(
  user: T,
): T & { workspaceAccess: WorkspaceAccess } => ({
  ...user,
  workspaceAccess: deriveWorkspaceAccess(user.roles),
});

const getUserWorkspaceAccess = (
  user:
    | { roles: readonly string[]; workspaceAccess?: WorkspaceAccess }
    | null
    | undefined,
): WorkspaceAccess => {
  if (!user) {
    return deriveWorkspaceAccess([]);
  }

  return user.workspaceAccess ?? deriveWorkspaceAccess(user.roles);
};

export function useAuthMutations<
  T = unknown,
  L = LoginCredentials,
  R = RegisterData,
>(options: AuthHooksOptions<T, L, R>) {
  const {
    authStore,
    loginFn,
    registerFn,
    onError,
    onLoginError,
    onRegisterError,
  } = options;

  const loginMutation = useMutation<LoginResult<T>, Error, L>({
    mutationKey: ["login"],
    mutationFn: loginFn,
    onSuccess: (data) => {
      applyLoginResultToAuthStore(authStore, data);
    },
    onError: (error) => {
      authStore.clearAuthState();
      authStore.clearMfaState?.();
      (onLoginError ?? onError)?.(error);
    },
  });

  const registerMutation = useMutation<void, Error, R>({
    mutationKey: ["register"],
    mutationFn: registerFn,
    onError: (error) => {
      authStore.clearAuthState();
      (onRegisterError ?? onError)?.(error);
    },
  });

  return {
    loginMutation,
    registerMutation,
  };
}

export function createUseAuthMutations(
  options: CreateAuthHooksOptions,
) {
  return useAuthMutations<UserDetailDTO>({
    authStore: {
      ...options.authStore,
      setUserData: (user) => {
        options.authStore.setUserData(normalizeUserWorkspaceAccess(user));
      },
    },
    loginFn: options.accountApi.login,
    registerFn: options.accountApi.register,
    onError: options.onError,
    onLoginError: options.onLoginError,
    onRegisterError: options.onRegisterError,
  });
}

export function useGetCurrentUserQuery<T = unknown>(
  getCurrentUserFn: () => Promise<T>,
  options?: Omit<UseQueryOptions<T, Error>, "queryKey" | "queryFn">,
) {
  return useQuery<T, Error>({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: getCurrentUserFn,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function createCurrentUserQueryFn(
  accountApi: Pick<AccountApi, "getCurrentUser">,
) {
  return async (): Promise<UserDetailDTO> => {
    const response = await accountApi.getCurrentUser();

    if (!response.payload) {
      throw new Error("Current user payload is missing");
    }

    return normalizeUserWorkspaceAccess(response.payload);
  };
}

export const getCurrentUserWorkspaceAccess = getUserWorkspaceAccess;

export function createCurrentUserQueryOptions(
  accountApi: Pick<AccountApi, "getCurrentUser">,
  options?: Omit<
    UseQueryOptions<
      UserDetailDTO,
      Error,
      UserDetailDTO,
      typeof CURRENT_USER_QUERY_KEY
    >,
    "queryKey" | "queryFn"
  >,
): UseQueryOptions<
  UserDetailDTO,
  Error,
  UserDetailDTO,
  typeof CURRENT_USER_QUERY_KEY
> {
  return {
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: createCurrentUserQueryFn(accountApi),
    staleTime: 5 * 60 * 1000,
    ...options,
  };
}

export function createUseGetCurrentUserQuery(
  accountApi: Pick<AccountApi, "getCurrentUser">,
  options?: Omit<
    UseQueryOptions<
      UserDetailDTO,
      Error,
      UserDetailDTO,
      typeof CURRENT_USER_QUERY_KEY
    >,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery(createCurrentUserQueryOptions(accountApi, options));
}
